import React, { FC, useEffect, useState } from 'react';
import {
  Alert,
  Card,
  Flex,
  Pagination,
  Switch,
  Typography,
} from '@duxton-web/ui';
import { Empty, Skeleton } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { Link, useLocation } from 'react-router-dom';

import { useAppNavigate } from '../../utils/navigation';
import ChangelogAll from './ChangelogAll';
import ChangelogIndividual from './ChangelogIndividual';
import { ObjectDetailPage } from '@/common/components';
import FilterDrawer, {
  ConstraintFilterKey,
} from '@/common/components/FilterDrawer/FilterDrawer';
import {
  ChangelogType,
  GlobalChangelogFiltersGroup,
  VariableChangelogFiltersGroup,
  VariableOnlyChangelogFilters,
} from '@/common/const';
import { VariableState } from '@/constants';
import { DEFAULT_DATE_FORMAT, SHORT_DATE_TIME } from '@/constants/dateTime';
import { useVariable } from '@/contexts/VariableContext';
import Service from '@/lib/App';
import { Rollout, VariableInfo } from '@/lib/PlannerDTOs';
import { VARIABLE_PAGE_SIZE } from '@/store/state/variables/actions';
import { transformDateToUTCString } from '@/utils/dateTime';

interface IChangelogProps {
  onRestore?: (rollout: Rollout) => void;
}

type ChangelogFilterState = {
  dates: [Dayjs, Dayjs];
  variableName?: string;
  changelogType?: ChangelogType;
  approvedBy?: string;
  changedBy?: string;
  constraint?: {
    [ConstraintFilterKey.FACET_TYPE]: string;
    [ConstraintFilterKey.FACET_VALUE]: string;
  };
  createdBy?: string;
  productOwner?: string;
  productEntity?: string;
  technicalOwner?: string;
  techFamily?: string;
  domain?: string;
  backend?: string;
};

const numberOfYearsBefore = 1;
const numberOfWeeksBefore = 1;
function getDefaultDateRange(
  variable: VariableInfo,
): [dayjs.Dayjs, dayjs.Dayjs] {
  return [getStartDate(dayjs(), variable).startOf('day'), dayjs().endOf('day')];
}

function isTimeRangeExceeded(
  dates: [Dayjs, Dayjs],
  variable: VariableInfo,
): boolean {
  if (variable) {
    return dates[0].isBefore(dates[1].startOf('day').subtract(1, 'year'));
  }
  return dates[0].isBefore(dates[1].startOf('day').subtract(1, 'week'));
}

function getStartDate(date: Dayjs, variable?: VariableInfo) {
  if (!variable)
    return date.subtract(numberOfWeeksBefore, 'week').add(1, 'day');

  return date.subtract(numberOfYearsBefore, 'year').add(1, 'day');
}

function EmptyState({ filters, variable }) {
  const startDate = filters.dates[0];
  const endDate = filters.dates[1];
  const defaultDateRange = getDefaultDateRange(variable);
  const defaultStartDate = defaultDateRange[0];
  const defaultEndDate = defaultDateRange[1];
  let description: string = `No changes made in the last ${
    variable ? 'year' : 'week'
  }`;

  if (!startDate.isSame(defaultStartDate) || !endDate.isSame(defaultEndDate))
    description = 'No changes were made for the selected period';

  return (
    <div
      style={{
        flexGrow: 1,
        overflow: 'hidden auto',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Empty description={description} image={Empty.PRESENTED_IMAGE_SIMPLE} />
    </div>
  );
}

function ExceedRangeAlert({
  filters,
  variable,
}: {
  filters: ChangelogFilterState;
  variable: VariableInfo;
}) {
  const selectedDates: [dayjs.Dayjs, dayjs.Dayjs] = filters.dates
    ? [dayjs(filters.dates[0]), dayjs(filters.dates[1])]
    : getDefaultDateRange(variable);

  if (!isTimeRangeExceeded(selectedDates, variable)) return <></>;

  return (
    <Alert
      type="info"
      message={
        <div
          style={{
            lineHeight: '20px',
          }}
        >
          <Typography.Text>
            The selected date range exceeds a {numberOfYearsBefore}{' '}
            {variable ? 'year(s)' : 'week(s)'} range. Only data for the last{' '}
            {numberOfYearsBefore} {variable ? 'year(s)' : 'week(s)'} (
            <Typography.Text strong>
              {`${getStartDate(filters.dates[1], variable).format(
                DEFAULT_DATE_FORMAT,
              )} - ${filters.dates[1].format(DEFAULT_DATE_FORMAT)}`}
            </Typography.Text>
            ) of the selected date will be returned.
          </Typography.Text>
        </div>
      }
    />
  );
}

function DiffToggle({ showDiffOnly, isLoading, setShowDiffOnly }) {
  const [togglingShowDiff, setTogglingShowDiff] = useState(false);

  return (
    <Flex
      gap={8}
      align="center"
      style={{
        padding: '6px 14px',
        borderRadius: 48,
      }}
    >
      <Switch
        checked={showDiffOnly}
        onChange={(checked) => {
          setTogglingShowDiff(true);
          setShowDiffOnly(checked);
          setTimeout(() => setTogglingShowDiff(false), 1000);
        }}
        loading={isLoading || togglingShowDiff}
        disabled={isLoading || togglingShowDiff}
      />
      <Typography.Body level={3} style={{ lineHeight: 0 }} type="secondary">
        Show diff only
      </Typography.Body>
    </Flex>
  );
}

function Footer({ pageNumber, changelogs, total, paginationChange }) {
  if (!changelogs?.length) return <></>;

  return (
    <Pagination
      style={{
        justifyContent: 'flex-end',
        borderTop: '1px solid #E8E8E8',
        padding: 16,
        flex: '0 1 65px',
      }}
      showSizeChanger={false}
      total={total}
      pageSize={VARIABLE_PAGE_SIZE}
      current={pageNumber}
      onChange={(pageNumber) => {
        paginationChange(pageNumber);
      }}
      showTotal={(total: number, range: [number, number]) => {
        return <>{`${range?.[0]} - ${range?.[1]} of ${total}`}</>;
      }}
    />
  );
}

function ChangelogContent({
  isLoading,
  filters,
  changelogs,
  variable,
  latestRollout,
  showDiffOnly,
  onRestore,
}) {
  const items = [];

  if (isLoading) {
    for (let i = 0; i < 7; i++) {
      items.push(
        <Skeleton
          key={`changelog-loader-${i}`}
          active
          paragraph={{
            rows: 7,
          }}
        />,
      );
    }

    return (
      <div
        style={{
          flex: '1 1 auto',
          overflow: 'hidden',
          padding: '0 16px',
        }}
      >
        {items}
      </div>
    );
  }

  if (!changelogs?.length) {
    return <EmptyState filters={filters} variable={variable} />;
  }

  if (variable) {
    return (
      <ChangelogIndividual
        latestRollout={latestRollout}
        changelogs={changelogs}
        onRestore={onRestore}
        includeUnchanged={!showDiffOnly}
      />
    );
  }

  return (
    <ChangelogAll changelogs={changelogs} includeUnchanged={!showDiffOnly} />
  );
}

function UI({
  changelogs,
  latestRollout,
  pageNumber,
  showDiffOnly,
  setShowDiffOnly,
  isLoading,
  filters,
  total,
  variable,
  handleApplyFilter,
  onRestore,
  paginationChange,
}) {
  const changelogContent = (
    <Card
      id="changelog-container"
      styles={{
        body: {
          height: '100%',
          padding: 0,
        },
      }}
    >
      <Flex
        vertical
        gap={16}
        style={{
          paddingTop: 16,
          height: '100%',
        }}
      >
        <ChangelogContent
          filters={filters}
          isLoading={isLoading}
          changelogs={changelogs}
          variable={variable}
          latestRollout={latestRollout}
          showDiffOnly={showDiffOnly}
          onRestore={onRestore}
        />
        <Footer
          changelogs={changelogs}
          pageNumber={pageNumber}
          total={total}
          paginationChange={paginationChange}
        />
      </Flex>
    </Card>
  );

  if (variable)
    return (
      <Flex
        vertical
        gap={8}
        style={{
          overflow: 'hidden',
          maxHeight: 'calc(100vh - 148px)',
          height: '100%',
        }}
      >
        <Flex justify="flex-end">
          <DiffToggle
            isLoading={isLoading}
            showDiffOnly={showDiffOnly}
            setShowDiffOnly={setShowDiffOnly}
          />
          <FilterDrawer
            groups={VariableChangelogFiltersGroup}
            onApply={handleApplyFilter}
            filters={filters}
          />
        </Flex>
        <ExceedRangeAlert variable={variable} filters={filters} />
        {changelogContent}
      </Flex>
    );

  return (
    <ObjectDetailPage
      style={{
        paddingRight: 24,
        paddingLeft: 24,
        paddingBottom: 24,
      }}
    >
      <Flex
        vertical
        gap={16}
        style={{
          height: '100%',
          overflow: 'hidden',
        }}
      >
        <Flex justify="space-between" align="center" style={{ marginTop: 16 }}>
          <Typography.Title
            level={3}
            style={{
              flex: '0 1 auto',
            }}
          >
            <Link to="variables">Variables</Link> / Changelog
          </Typography.Title>
          <Flex>
            <DiffToggle
              isLoading={isLoading}
              showDiffOnly={showDiffOnly}
              setShowDiffOnly={setShowDiffOnly}
            />
            <FilterDrawer
              groups={GlobalChangelogFiltersGroup}
              onApply={handleApplyFilter}
              filters={filters}
            />
          </Flex>
        </Flex>
        <ExceedRangeAlert variable={variable} filters={filters} />
        {changelogContent}
      </Flex>
    </ObjectDetailPage>
  );
}

const Changelog: FC<IChangelogProps> = ({ onRestore }) => {
  const { navigateTo } = useAppNavigate();
  const location = useLocation();
  const variable = useVariable();

  const [isLoading, setIsLoading] = useState(false);
  const [changelogs, setChangelogs] = useState([]);
  const [total, setTotal] = useState<number>(0);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [latestRollout, setLatestRollout] = useState<Rollout>(null);

  const [showDiffOnly, setShowDiffOnly] = useState(true);

  const [filters, setFilters] = useState<ChangelogFilterState>({
    dates: getDefaultDateRange(variable),
  });

  function fetchChangelogs({
    name,
    pageNumber,
    dateStrings,
    filters,
  }: {
    name: string;
    pageNumber: number;
    dateStrings: [string, string];
    filters?: ChangelogFilterState;
  }) {
    const payload: Record<string, any> = {
      name,
      start_date: dateStrings[0],
      end_date: dateStrings[1],
      limit: VARIABLE_PAGE_SIZE,
      offset: (pageNumber - 1) * VARIABLE_PAGE_SIZE,
    };

    if (filters?.changelogType === ChangelogType.ROLLOUT) {
      payload.rollout_only_filter = true;
    } else if (filters?.changelogType === ChangelogType.VARIABLE) {
      payload.variable_only_filter = true;
    }

    if (filters?.approvedBy) payload.approved_by = filters.approvedBy;
    if (filters?.changedBy) payload.changed_by = filters.changedBy;

    if (filters?.constraint) {
      payload.facet_type = filters.constraint[ConstraintFilterKey.FACET_TYPE];
      payload.facet_value = filters.constraint[ConstraintFilterKey.FACET_VALUE];
    }

    const variableFilter: Record<string, any> = {};
    const variableFilterKeys = VariableOnlyChangelogFilters.map(
      (filter) => filter.name,
    );

    variableFilterKeys.forEach((key) => {
      if (filters?.[key]) variableFilter[key] = filters[key];
    });

    if (Object.keys(variableFilter).length > 0) {
      payload.variable_filters = variableFilter;
    }

    if (variable) return Service.planner.getVariableChangeLogs(payload);

    return Service.planner.getChangeLogs(payload);
  }

  function handleApplyFilter(filters) {
    setPageNumber(1);
    const selectedDates: [dayjs.Dayjs, dayjs.Dayjs] = filters.dates
      ? [dayjs(filters.dates[0]), dayjs(filters.dates[1])]
      : getDefaultDateRange(variable);
    setFilters({
      ...filters,
      dates: selectedDates,
    });

    refreshChangelog({
      name: variable?.name || filters.variableName,
      pageNumber: 1,
      inDates: selectedDates,
      filters,
    });

    if (filters.dates) {
      navigateTo(
        `?start=${selectedDates[0].format(
          SHORT_DATE_TIME,
        )}&end=${selectedDates[1].format(SHORT_DATE_TIME)}`,
      );
    } else {
      navigateTo(`?`);
    }
  }

  function paginationChange(pageNumber) {
    setPageNumber(pageNumber);

    const selectedDates: [dayjs.Dayjs, dayjs.Dayjs] = filters.dates
      ? [dayjs(filters.dates[0]), dayjs(filters.dates[1])]
      : getDefaultDateRange(variable);

    refreshChangelog({
      name: variable?.name || filters.variableName,
      pageNumber: pageNumber,
      inDates: selectedDates,
      filters,
    });

    if (filters.dates) {
      navigateTo(
        `?start=${selectedDates[0].format(
          SHORT_DATE_TIME,
        )}&end=${selectedDates[1].format(SHORT_DATE_TIME)}`,
      );
    } else {
      navigateTo(`?`);
    }
  }

  async function getChangelogs({
    name,
    pageNumber,
    inDates,
    filters,
  }: {
    name: string;
    pageNumber: number;
    inDates: [Dayjs, Dayjs];
    filters?: ChangelogFilterState;
  }) {
    setIsLoading(true);

    let startDate = inDates[0];
    const endDate = inDates[1];
    if (startDate.isBefore(getStartDate(endDate, variable), 'date')) {
      startDate = getStartDate(endDate, variable).startOf('day');
    }

    try {
      const res = await fetchChangelogs({
        name,
        pageNumber,
        dateStrings: [
          transformDateToUTCString(startDate),
          transformDateToUTCString(endDate),
        ],
        filters,
      });

      setChangelogs(res?.data?.items || []);
      setTotal(res?.data?.total || 0);

      setIsLoading(false);
    } catch (e) {
      setTotal(0);
      setChangelogs([]);

      setIsLoading(false);
    }
  }

  async function getRollout(variableName: string) {
    setIsLoading(true);

    try {
      const res = await Service.planner.fetchRollout(variableName);

      setLatestRollout(res || null);

      setTimeout(() => setIsLoading(false), 1000);
    } catch (e) {
      setLatestRollout(null);

      setTimeout(() => setIsLoading(false), 1000);
    }
  }

  async function refreshChangelog({
    name,
    pageNumber,
    inDates,
    filters,
  }: {
    name: string;
    pageNumber: number;
    inDates: [Dayjs, Dayjs];
    filters?: ChangelogFilterState;
  }) {
    setIsLoading(true);

    await getChangelogs({ name, pageNumber, inDates, filters });

    setIsLoading(false);
  }

  async function refreshView(variable: VariableInfo, inDates?: [Dayjs, Dayjs]) {
    setIsLoading(true);

    await getChangelogs({
      name: variable?.name || filters.variableName,
      pageNumber,
      inDates: inDates || filters.dates,
      filters,
    });

    if (variable?.name && variable?.state !== VariableState.ARCHIVED)
      await getRollout(variable.name);

    setIsLoading(false);
  }

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const startDate = urlParams.get('start');
    const endDate = urlParams.get('end');

    if (startDate && endDate) {
      const dateRange: [dayjs.Dayjs, dayjs.Dayjs] = [
        dayjs(startDate, { format: SHORT_DATE_TIME }),
        dayjs(endDate, { format: SHORT_DATE_TIME }),
      ];

      setFilters({
        ...filters,
        dates: dateRange,
      });

      refreshView(variable, dateRange);

      return;
    }

    refreshView(variable);
  }, [variable]);

  return (
    <UI
      pageNumber={pageNumber}
      changelogs={changelogs}
      latestRollout={latestRollout}
      showDiffOnly={showDiffOnly}
      setShowDiffOnly={setShowDiffOnly}
      isLoading={isLoading}
      filters={filters}
      total={total}
      variable={variable}
      handleApplyFilter={handleApplyFilter}
      onRestore={onRestore}
      paginationChange={paginationChange}
    />
  );
};

export default Changelog;
